import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

export interface ProfileRoast {
  id: string;
  slug: string;
  url: string;
  site_name: string | null;
  screenshot_url: string | null;
  status: string;
  reveal_date: string;
  scores: {
    overall?: number;
    [key: string]: unknown;
  };
  upvote_count: number;
  comment_count: number;
  submitted_at: string;
}

interface IGetProfileRoastsQueryFnParams {
  supabase: SupabaseClient<Database>;
  userId: string;
}

const getProfileRoastsQueryFn = async ({
  supabase,
  userId,
}: IGetProfileRoastsQueryFnParams): Promise<ProfileRoast[]> => {
  const { data, error } = await supabase
    .from('roast')
    .select(
      'id, slug, url, site_name, screenshot_url, status, reveal_date, scores, upvote_count, comment_count, submitted_at'
    )
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (error) throw error;

  return (data ?? []) as unknown as ProfileRoast[];
};

interface IGetProfileRoastsQueryParams {
  userId?: string | null;
}

interface IGetProfileRoastsQueryResponse {
  data: ProfileRoast[] | null;
  isPending: boolean;
}

export const useGetProfileRoastsQuery = ({
  userId,
}: IGetProfileRoastsQueryParams): IGetProfileRoastsQueryResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetProfileRoasts, userId],
    queryFn: () =>
      getProfileRoastsQueryFn({
        supabase,
        userId: userId!,
      }),
    enabled: Boolean(userId),
  });

  return {
    data: data ?? null,
    isPending,
  };
};
